import "@/App.css";
import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useCustomDomain } from "@/hooks/useCustomDomain";
import { Toaster } from "@/components/ui/sonner";
import * as Sentry from "@sentry/react";
import { X, Download } from 'lucide-react';

// Force non-www redirect (besume.com only, not custom domains)
if (window.location.hostname === 'www.besume.com') {
  window.location.replace(window.location.href.replace('www.besume.com', 'besume.com'));
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Capture any ?via= or ?ref= query param on page load and stash it in
 * localStorage so the attribution survives nav across the session (user
 * lands on besume.com/joe?via=joe → browses to /signup → posts the value
 * as `ref` to the signup endpoint). Cleared once consumed at signup time.
 */
function CaptureAttribution() {
  React.useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const slug = p.get('via') || p.get('ref');
      if (slug && /^[a-z0-9][a-z0-9-]{0,60}$/i.test(slug)) {
        window.localStorage.setItem('besume_via', slug);
      }
    } catch { /* storage disabled — attribution simply won't persist */ }
  }, []);
  return null;
}

function ShopRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}/storefront`} replace />;
}

// Critical path — loaded eagerly
import LandingPage from "@/pages/LandingPage";

// Everything else — lazy loaded
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const WizardPage = lazy(() => import("@/pages/WizardPage"));
const TourPreviewPage = lazy(() => import("@/pages/TourPreviewPage"));
const PostInterviewVideo = lazy(() => import("@/components/PostInterviewVideo"));
const ProfileTab = lazy(() => import("@/pages/ProfileTab"));
const ShareTab = lazy(() => import("@/pages/ShareTab"));
const SellTab = lazy(() => import("@/pages/SellTab"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const EndorsementsPage = lazy(() => import("@/pages/EndorsementsPage"));
const InboxPage = lazy(() => import("@/pages/MessagesPage"));
const NetworkTab = lazy(() => import("@/pages/NetworkTab"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const SubscriptionPage = lazy(() => import("@/pages/settings/SubscriptionPage"));
const PublicProfilePage = lazy(() => import("@/pages/PublicProfilePage"));
const EndorseFormPage = lazy(() => import("@/pages/EndorseFormPage"));
const RoadmapPage = lazy(() => import("@/pages/RoadmapPage"));
const BrowsePage = lazy(() => import("@/pages/BrowsePage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const WizardPromptLabPage = lazy(() => import("@/pages/admin/WizardPromptLabPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const AcceptableUsePage = lazy(() => import("@/pages/AcceptableUsePage"));
const DMCAPage = lazy(() => import("@/pages/DMCAPage"));
const CelebrationPage = lazy(() => import("@/pages/CelebrationPage"));
const ProPage = lazy(() => import("@/pages/ProPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const WebhookGuidePage = lazy(() => import("@/pages/WebhookGuidePage"));
const HowToCustomDomainPage = lazy(() => import("@/pages/HowToCustomDomainPage"));
const MarketplaceTermsPage = lazy(() => import("@/pages/MarketplaceTermsPage"));
const ReportPage = lazy(() => import("@/pages/ReportPage"));
const VerifyEmailChangePage = lazy(() => import("@/pages/VerifyEmailChangePage"));
const RedeemPage = lazy(() => import("@/pages/RedeemPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const FreeClassPage = lazy(() => import("@/pages/FreeClassPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const WishlistTab = lazy(() => import("@/pages/WishlistTab"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const UserUpdatesPage = lazy(() => import("@/pages/UserUpdatesPage"));
const PostPage = lazy(() => import("@/pages/PostPage"));
const UserPostsPage = lazy(() => import("@/pages/UserPostsPage"));

// Sell sub-pages
const QuickBuildPage = lazy(() => import("@/pages/sell/QuickBuildPage"));
const GuidedSetupPage = lazy(() => import("@/pages/sell/GuidedSetupPage"));
const BulkUploadPage = lazy(() => import("@/pages/sell/BulkUploadPage"));
const BlankListingPage = lazy(() => import("@/pages/sell/BlankListingPage"));
const ListingPreviewPage = lazy(() => import("@/pages/sell/ListingPreviewPage"));
const SearchProductsPage = lazy(() => import("@/pages/SearchProductsPage"));

// Components
import CookieBanner from "@/components/CookieBanner";
import DashboardSidebar from "@/components/DashboardSidebar";

// Sentry Error Boundary Fallback
const SentryFallback = ({ error, resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#fafafa] p-6">
    <div className="max-w-md text-center">
      <h1 className="text-2xl font-serif font-bold mb-4">Something went wrong</h1>
      <p className="text-[#555] mb-6">We've been notified and are looking into it.</p>
      <button 
        onClick={() => window.location.href = '/'}
        className="px-6 py-3 bg-[#0a0a0a] text-white text-sm uppercase tracking-widest"
      >
        Go Home
      </button>
    </div>
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-serif font-black tracking-tight">besume</h1>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-serif font-black tracking-tight">besume</h1>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Minimal loading fallback — matches the app's editorial tone
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <h1 className="text-2xl font-serif font-black tracking-tight">besume</h1>
    </div>
  </div>
);

/**
 * V5.24.0 — Renders <PostInterviewVideo /> when ?tour=1 is in the URL
 * AND we're on a /dashboard or /settings route. Lives inside Router
 * context so it can read useSearchParams. Self-gating; no external
 * mount logic. Replaces the V5.22-V5.23 Shepherd.js guided tour with
 * a 1:46 video walkthrough — same trigger, same gate, same backend
 * (mark-tour-completed unchanged).
 */
function TourMount() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const showTour = searchParams.get('tour') === '1';
  // Guard the host route too — we never want the tour overlaying a
  // public profile or login page even if someone tampered with the URL.
  const onTourSurface = (
    location.pathname.startsWith('/dashboard') ||
    location.pathname === '/settings' ||
    location.pathname.startsWith('/settings/')
  );
  if (!showTour || !onTourSurface) return null;
  return <PostInterviewVideo />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pro" element={<ProPage />} />
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/settings/verify-email" element={<VerifyEmailChangePage />} />
      <Route path="/endorse/:token" element={<EndorseFormPage />} />
      
      {/* Legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/dmca" element={<DMCAPage />} />
      <Route path="/faq" element={<FaqPage />} />
            <Route path="/how-to/webhook" element={<WebhookGuidePage />} />
            <Route path="/support" element={<SupportPage />} />
      <Route path="/how-to/custom-domain" element={<HowToCustomDomainPage />} />
      <Route path="/marketplace" element={<MarketplaceTermsPage />} />
      <Route path="/report" element={<ReportPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/redeem" element={<RedeemPage />} />
      <Route path="/free-class" element={<FreeClassPage />} />
      
      {/* Browse/Discover routes */}
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/search/products" element={<SearchProductsPage />} />
      <Route path="/browse/:category" element={<BrowsePage />} />
      
      {/* Legacy product URL — redirect to browse */}
      <Route path="/product/:listingId" element={<Navigate to="/browse" replace />} />
      
      {/* Checkout pages */}
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      
      {/* Admin route - requires auth */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/wizard-lab" element={<ProtectedRoute><WizardPromptLabPage /></ProtectedRoute>} />
      
      {/* Protected routes */}
      <Route path="/interview" element={<ProtectedRoute><WizardPage /></ProtectedRoute>} />
      <Route path="/wizard" element={<Navigate to="/interview" replace />} />
      {/* V5.21.1 — QA-only route, server-gated by SHOW_TOUR_ALWAYS env */}
      <Route path="/preview/tour" element={<ProtectedRoute><TourPreviewPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/dashboard/profile" replace />} />
      <Route path="/dashboard/profile" element={<ProtectedRoute><ProfileTab /></ProtectedRoute>} />
      <Route path="/dashboard/share" element={<ProtectedRoute><ShareTab /></ProtectedRoute>} />
      <Route path="/dashboard/sell" element={<ProtectedRoute><SellTab /></ProtectedRoute>} />
      <Route path="/dashboard/sell/new/quick" element={<ProtectedRoute><QuickBuildPage /></ProtectedRoute>} />
      <Route path="/dashboard/sell/new/guided" element={<ProtectedRoute><GuidedSetupPage /></ProtectedRoute>} />
      <Route path="/dashboard/sell/new/bulk" element={<ProtectedRoute><BulkUploadPage /></ProtectedRoute>} />
      <Route path="/dashboard/sell/new/blank" element={<ProtectedRoute><BlankListingPage /></ProtectedRoute>} />
      <Route path="/dashboard/sell/preview/:id" element={<ProtectedRoute><ListingPreviewPage /></ProtectedRoute>} />
      <Route path="/dashboard/network" element={<ProtectedRoute><NetworkTab /></ProtectedRoute>} />
      <Route path="/dashboard/saved" element={<Navigate to="/dashboard/share" replace />} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/dashboard/endorsements" element={<Navigate to="/dashboard/share" replace />} />
      <Route path="/dashboard/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/dashboard/messages" element={<Navigate to="/dashboard/inbox" replace />} />
      <Route path="/dashboard/leads" element={<Navigate to="/dashboard/inbox" replace />} />
      <Route path="/dashboard/posts" element={<Navigate to="/dashboard/network" replace />} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route path="/celebration/:slug" element={<ProtectedRoute><CelebrationPage /></ProtectedRoute>} />
      
      {/* Public profile - must be last due to dynamic slug */}
      <Route path="/:slug/shop" element={<ShopRedirect />} />
      <Route path="/:slug/storefront" element={<ShopPage />} />
      <Route path="/:slug/storefront/:productSlug" element={<ProductPage />} />
      <Route path="/:slug/updates" element={<UserUpdatesPage />} />
      <Route path="/:slug/posts" element={<UserPostsPage />} />
      <Route path="/:slug/posts/:postSlug" element={<PostPage />} />
      <Route path="/:slug" element={<PublicProfilePage />} />
      
      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

function CustomDomainApp() {
  const { isCustomDomain, slug, loading } = useCustomDomain();

  if (!isCustomDomain) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <CaptureAttribution />
        <AppRoutes />
        <Suspense fallback={null}><TourMount /></Suspense>
        <CookieBanner />
        <Toaster position="bottom-right" />
      </BrowserRouter>
    );
  }

  if (loading) {
    return <PageLoader />;
  }

  if (!slug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-serif font-bold mb-4">Profile not found</h1>
          <p className="text-[#555] mb-6">No Besume profile is connected to this domain.</p>
          <a href="https://besume.com" className="px-6 py-3 bg-[#0a0a0a] text-white text-sm uppercase tracking-widest inline-block">
            Visit Besume
          </a>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="*" element={<PublicProfilePage overrideSlug={slug} />} />
        </Routes>
      </Suspense>
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}

const PWAInstallBanner = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('pwa_banner_dismissed')) return;
    if (window.innerWidth > 768) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    setIsIOS(ios);

    if (ios) {
      const t = setTimeout(() => setShowBanner(true), 10000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') dismiss();
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed', '1');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0a] text-white px-4 py-3 flex items-center justify-between z-50 shadow-lg" data-testid="pwa-install-banner">
      <p className="text-sm flex-1 mr-3">
        {isIOS
          ? 'Add Besume to your home screen \u2014 tap Share then "Add to Home Screen"'
          : 'Add Besume to your home screen for quick access'}
      </p>
      {!isIOS && (
        <button onClick={handleInstall} className="bg-white text-black text-sm font-semibold px-4 py-2 rounded-full flex items-center gap-2 mr-2 whitespace-nowrap" data-testid="pwa-install-btn">
          <Download size={16} /> Install
        </button>
      )}
      <button onClick={dismiss} className="text-white/60 hover:text-white p-1" data-testid="pwa-dismiss-btn">
        <X size={20} />
      </button>
    </div>
  );
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <PWAInstallBanner />
          <CustomDomainApp />
        </AuthProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
